import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { RestaurantService } from '../../../../services/restaurant.service';
import { FNFResponse } from '../../../../models/response';

@Component({
  selector: 'app-zone-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIcon],
  templateUrl: './zone-form.component.html',
  styleUrl: './zone-form.component.css',
})
export class ZoneFormComponent {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private spinner = inject(NgxSpinnerService);
  restaurantService = inject(RestaurantService);

  /** Set on the edit route; null when adding. */
  zone: any = null;
  allZones: any[] = [];
  loading = true;
  isDuplicate = false;

  /** Type names the zone carries. Free text and picked chips share this list. */
  assignedTypes: string[] = [];
  /** Chip options drawn from Types & Categories. */
  typeOptions: string[] = [];
  customType = '';
  typeError = '';

  readonly maxTypes = 6;

  zoneForm: FormGroup = this.buildForm(null);

  ngOnInit() {
    this.route.paramMap.subscribe((params) => this.load(params.get('id')));
  }

  private load(zoneId: string | null) {
    this.loading = true;
    this.restaurantService.getRestaurantZones().subscribe({
      next: (zoneRes: FNFResponse<any>) => {
        this.allZones = zoneRes?.data || [];
        this.zone = zoneId
          ? this.allZones.find((z: any) => String(z.id) === String(zoneId)) || null
          : null;
        this.zoneForm = this.buildForm(this.zone);
        this.assignedTypes = [...(this.zone?.location_types || [])];

        // Chips come from the restaurant's own types; a failure here should
        // not block the form, since types can still be typed in by hand.
        this.restaurantService.getRestaurantTypeAndCategories().subscribe({
          next: (typeRes: FNFResponse<any>) => {
            this.typeOptions = (typeRes?.data || []).map((t: any) => t.name).filter(Boolean);
            this.loading = false;
          },
          error: () => {
            this.typeOptions = [];
            this.loading = false;
          },
        });
      },
      error: () => {
        this.toastr.error('Unable to load the zone. Please try again later.', 'Error');
        this.loading = false;
        this.cancel();
      },
    });
  }

  private buildForm(zone: any): FormGroup {
    return this.fb.group({
      zone_name: [
        zone?.zone_name ?? '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
      ],
      capacity: [zone?.capacity ?? null, [Validators.min(1), Validators.max(500)]],
      floor: [zone?.floor ?? null, [Validators.min(0), Validators.max(10)]],
      wheelchair_accessibility: [zone?.wheelchair_accessibility ?? false],
    });
  }

  // ------------------------------------------------------------------ types

  isAssigned(name: string): boolean {
    return this.assignedTypes.some((t) => t.toLowerCase() === name.toLowerCase());
  }

  get atTypeLimit(): boolean {
    return this.assignedTypes.length >= this.maxTypes;
  }

  toggleType(name: string) {
    this.typeError = '';
    const index = this.assignedTypes.findIndex((t) => t.toLowerCase() === name.toLowerCase());
    if (index >= 0) {
      this.assignedTypes.splice(index, 1);
      return;
    }
    if (this.atTypeLimit) {
      this.typeError = `Select up to ${this.maxTypes} types for this zone.`;
      return;
    }
    this.assignedTypes.push(name);
  }

  /** Types not offered as chips still need a way in and a way out. */
  get customTypes(): string[] {
    return this.assignedTypes.filter(
      (t) => !this.typeOptions.some((o) => o.toLowerCase() === t.toLowerCase()),
    );
  }

  addCustomType() {
    const name = this.customType.trim();
    if (!name) {
      return;
    }
    if (this.isAssigned(name)) {
      this.typeError = 'That type is already assigned.';
      return;
    }
    if (this.atTypeLimit) {
      this.typeError = `Select up to ${this.maxTypes} types for this zone.`;
      return;
    }
    this.assignedTypes.push(name);
    this.customType = '';
    this.typeError = '';
  }

  removeType(name: string) {
    this.assignedTypes = this.assignedTypes.filter((t) => t !== name);
    this.typeError = '';
  }

  // ----------------------------------------------------------------- submit

  checkDuplicate() {
    const name = (this.zoneForm.get('zone_name')?.value || '').trim().toLowerCase();
    this.isDuplicate = this.allZones.some(
      (z: any) => z.zone_name?.trim().toLowerCase() === name && z.id !== this.zone?.id,
    );
  }

  /**
   * Reached from Zones as well as from Add Table's "Create zone now" prompt;
   * returnTo carries the caller so the user lands back where they started.
   */
  private get returnUrl(): string {
    return this.route.snapshot.queryParamMap.get('returnTo') || '/main/zone';
  }

  cancel() {
    this.router.navigateByUrl(this.returnUrl);
  }

  save() {
    if (this.zoneForm.invalid || this.isDuplicate) {
      return;
    }

    const payload: any = { ...this.zoneForm.value, location_types: this.assignedTypes };

    this.spinner.show();
    const request = this.zone
      ? this.restaurantService.editRestaurantZone(payload, this.zone.id)
      : this.restaurantService.addRestaurantZone(payload);

    request.subscribe({
      next: (response: FNFResponse<any>) => {
        this.spinner.hide();
        if (!response.data) {
          const message = response.error?.error?.message || response.error?.message;
          this.toastr.error(message || 'No data found!', 'Error');
          return;
        }
        this.toastr.success(
          this.zone ? 'Zone updated successfully' : 'Zone added successfully',
          'Success',
        );
        this.cancel();
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }
}
